import { FunctionComponent, useRef } from 'react';

import { Stack, Typography } from '@mui/material';
import { AttachFile, Delete } from '@mui/icons-material';

import { getDeviceFile } from '@dw/services/filePickerService';
import { useAuth, useConfirm, useFileUploader, useSnackbar } from '@dw/hooks';
import { useCreateBlobUploadLinkMutation } from '@dw/hooks/api/BlobApi';
import { BLOB_UPLOAD_TYPE } from '@dw/models/blob';
import { DEFAULT_ERROR_MESSAGE } from '@dw/constants';
import { formatFileSize, formatPercentage } from '@dw/utils';

import { Button } from './Button';
import { EditorListSection } from './EditorListSection';
import { HStack } from './HStack';
import { IconButton } from './IconButton';
import { ProgressSpinner } from './ProgressSpinner';

/** An uploaded attachment: enough to display, enforce size rules, and submit (via `key`). */
export interface IUploadedAttachment {
  key: string;
  fileName: string;
  sizeInBytes: number;
  fileSizeFormatted: string;
}

/** Either a shared total cap (e.g. 10MB across all files) or a per-file cap with a max count. */
export type AttachmentSizeRule =
  | { maxTotalMb: number }
  | { maxFiles: number; maxSizePerFileMb: number };

const isTotalCapRule = (rule: AttachmentSizeRule): rule is { maxTotalMb: number } => 'maxTotalMb' in rule;

/** Accepts an entry list like ['application/pdf', 'image/*'] — '/*' entries match by prefix. */
const isAcceptedMimeType = (contentType: string, acceptedMimeTypes: string[]): boolean =>
  acceptedMimeTypes.some(type => type.endsWith('/*') ? contentType.startsWith(type.slice(0, -1)) : contentType === type);

interface IAttachmentUploadListProps {
  value: IUploadedAttachment[];
  onChange: (attachments: IUploadedAttachment[]) => void;
  uploadType: BLOB_UPLOAD_TYPE;
  acceptedMimeTypes: string[];
  sizeRule: AttachmentSizeRule;
  disabled?: boolean;
  /** Require a confirmation dialog before removing an attachment. */
  confirmRemove?: boolean;
  /** Warning shown when a picked file's type isn't in `acceptedMimeTypes` — override when the accepted types differ from the default PDF/image/video set. */
  invalidTypeMessage?: string;
}

/**
 * Pick → validate → presign → upload → list attachments, with a per-row remove.
 * Owns the upload flow; the parent keeps the resulting `IUploadedAttachment[]` in its form state.
 */
export const AttachmentUploadList: FunctionComponent<IAttachmentUploadListProps> = (props: IAttachmentUploadListProps) => {

  const { value, onChange, uploadType, acceptedMimeTypes, sizeRule, disabled, confirmRemove, invalidTypeMessage } = props;

  // Latest attachments list for callbacks that fire after an await or a confirm dialog —
  // acting on the render-time `value` snapshot there would resurrect rows removed in the meantime.
  const valueRef = useRef(value);
  valueRef.current = value;

  const { showSnackbar } = useSnackbar();
  const { confirm } = useConfirm();
  const { danceSchool } = useAuth();

  const { uploadFile, isUploadingFile, fileUploadProgress } = useFileUploader();
  const { mutateAsync: createBlobUploadLink, isPending: createBlobUploadLinkLoading, variables: createBlobUploadLinkVariables } = useCreateBlobUploadLinkMutation();

  const atMaxFiles = !isTotalCapRule(sizeRule) && value.length >= sizeRule.maxFiles;
  const uploading = createBlobUploadLinkLoading || isUploadingFile;

  const caption = isTotalCapRule(sizeRule)
    ? `max ${sizeRule.maxTotalMb}MB in total`
    : `Up to ${sizeRule.maxFiles} files, ${sizeRule.maxSizePerFileMb}MB each`;

  const handleAdd = async () => {
    if (!isTotalCapRule(sizeRule) && value.length >= sizeRule.maxFiles) {
      showSnackbar(`You can attach up to ${sizeRule.maxFiles} files`, 'warning');
      return;
    }

    const deviceFile = await getDeviceFile(acceptedMimeTypes);
    if (!deviceFile) {
      return;
    }

    if (!isAcceptedMimeType(deviceFile.contentType, acceptedMimeTypes)) {
      showSnackbar(invalidTypeMessage ?? 'Only PDF, Images and Videos are allowed as attachments', 'warning');
      return;
    }

    if (isTotalCapRule(sizeRule)) {
      const existingSizeInKb = value.map(x => Math.ceil(x.sizeInBytes / 1024)).reduce((a, b) => a + b, 0);
      const newSizeInKb = Math.ceil(deviceFile.rawFile.size / 1024);
      if (newSizeInKb + existingSizeInKb > sizeRule.maxTotalMb * 1024) {
        showSnackbar(`Attachments cannot exceed ${sizeRule.maxTotalMb}mb in total`, 'warning');
        return;
      }
    } else if (deviceFile.rawFile.size > sizeRule.maxSizePerFileMb * 1024 * 1024) {
      showSnackbar(`Each attachment must be ${sizeRule.maxSizePerFileMb}MB or smaller`, 'warning');
      return;
    }

    const uploadLink = await createBlobUploadLink({
      contentType: deviceFile.contentType,
      fileName: deviceFile.fileName,
      uploadType
    });

    const uploaded = await uploadFile(uploadLink.signedUrl, deviceFile.rawFile, uploadLink.metadata);
    if (!uploaded) {
      showSnackbar(DEFAULT_ERROR_MESSAGE, 'error');
      return;
    }

    onChange([...valueRef.current, {
      key: uploadLink.key,
      fileName: deviceFile.fileName,
      sizeInBytes: deviceFile.rawFile.size,
      fileSizeFormatted: formatFileSize(deviceFile.rawFile.size)
    }]);
  };

  const handleRemove = (key: string) => {
    const removeAttachment = () => onChange(valueRef.current.filter(x => x.key !== key));
    if (confirmRemove) {
      confirm({
        title: 'Remove Attachment?',
        message: 'Are you sure you want to remove this attachment?',
        positiveText: 'Remove Attachment',
        onPositive: removeAttachment
      });
    } else {
      removeAttachment();
    }
  };

  return (
    <EditorListSection
      title='Attachments'
      caption={caption}
      action={(
        <Button
          startIcon={<AttachFile />}
          size='small'
          variant='text'
          onClick={handleAdd}
          loading={uploading}
          disabled={disabled || atMaxFiles}
        >
          Add Attachment
        </Button>
      )}
    >
      {(value.length > 0 || uploading) && (
        <Stack rowGap={1}>
          {value.map((attachment) => (
            <HStack breakpoint={false} justifyContent='space-between' key={attachment.key}>
              <Typography variant='body2'>
                {attachment.fileName} ({attachment.fileSizeFormatted})
              </Typography>

              <IconButton
                icon={<Delete fontSize='small' />}
                color='error'
                size='small'
                tooltip='Remove Attachment'
                onClick={() => handleRemove(attachment.key)}
                disabled={disabled}
              />
            </HStack>
          ))}

          {uploading && (
            <HStack breakpoint={false} justifyContent="space-between">
              <HStack breakpoint={false}>
                <ProgressSpinner size={20} />

                <Typography variant='body2' color='textDisabled'>
                  Processing "{createBlobUploadLinkVariables?.fileName}"
                </Typography>
              </HStack>

              {isUploadingFile && (
                <Typography variant='body2' color='textDisabled'>
                  {formatPercentage(fileUploadProgress, danceSchool?.countryCode)}
                </Typography>
              )}
            </HStack>
          )}
        </Stack>
      )}
    </EditorListSection>
  );
};
